import type { DelegateStatus, SessionStatus, ToolCall, UIMessage } from "./types";
import { currentDelegateRequestId, delegateDraftOptions, delegatePresentation } from "./delegatePresentation";
import { validArchive } from "./delegateTimeline";

type Queued = { id: string; userMsgId?: string; dispatching?: boolean };
// Only fixed native progress labels, never arbitrary Bash/Read/custom tools.
const MACHINE_TOOLS = new Set([
  "Codex shell task", "Codex Build command", "Codex file changes", "Codex Build file change",
  "Codex web research", "Codex Build web search", "Codex delegated task", "Codex Build agent",
  "Codex connected tool", "Codex Build tool", "Codex image task", "Codex image review",
  "Codex lane queue", "Codex Lane3 queue",
]);
export function isCollapsibleDelegateTool(tool: ToolCall, scope?: {runId:string;requestMessageId:string}) {
  return MACHINE_TOOLS.has(tool.toolName) || (!!scope &&
    tool.id.startsWith("delegate-progress:" + scope.runId + ":" + scope.requestMessageId + ":") &&
    /^[a-f0-9]{16}$/.test(tool.id.split(":").at(-1) ?? ""));
}
export function hasDelegateProxyMarker(tool: ToolCall) {
  return /^delegate-progress:[a-zA-Z0-9_-]+:[a-zA-Z0-9_-]+:[a-f0-9]{16}$/.test(tool.id);
}

/** A native execution receipt proves identity, never successful task completion. */
export function nativeExecutionForMessage(message: UIMessage) {
  const execution = message.meta?.delegateExecution;
  const id = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/;
  return execution?.source === "native-delegate-execution" && execution.model === "gpt-6-astra" &&
    typeof execution.runId === "string" && id.test(execution.runId) &&
    typeof execution.requestMessageId === "string" && id.test(execution.requestMessageId) &&
    ["high", "xhigh", "max"].includes(execution.rootEffort) &&
    ["completed", "failed", "cancelled"].includes(execution.status) ? execution : undefined;
}

/** Historical presentation only; an invoked command is not a completion receipt. */
export function delegatedRequestForMessage(messages: UIMessage[], queue: Queued[], messageId: string,
  app: "operations" | "build") {
  const index = messages.findIndex(message => message.id === messageId && message.role === "assistant");
  if (index < 0) return;
  const id = currentDelegateRequestId(messages.slice(0, index), queue);
  // Saved workflows can resolve their opt-in on the server without visible slash text.
  const execution = nativeExecutionForMessage(messages[index]!);
  if (id && execution?.requestMessageId === id) return id;
  const archive = validArchive(messages[index]!.meta?.delegateArchive);
  if (id && archive?.requestMessageId === id) return id;
  const request = messages.find(message => message.id === id);
  const command = request && delegateDraftOptions(request.content, app);
  return command && !command.invalid ? id : undefined;
}

/** Only the authenticated current runtime owns the live card and its technical rows. */
export function currentDelegateActivity(status: SessionStatus | undefined, companyId: string | null,
  messages: UIMessage[], queue: Queued[], streamingMessageId: string | null) {
  if (!companyId) return null;
  const requestId = currentDelegateRequestId(messages, queue);
  const view = delegatePresentation(status, companyId, requestId);
  const delegate = status?.delegate;
  if (!view?.canStop || !delegate || !/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,127}$/.test(delegate.runId)) return null;
  const index = messages.findIndex(message => message.id === streamingMessageId &&
    message.role === "assistant" && message.isStreaming);
  const message = index >= 0 && currentDelegateRequestId(messages.slice(0, index), queue) === requestId
    ? messages[index] : undefined;
  return { delegate, messageId: message?.id, technical: (message?.tools ?? []).filter(tool => isCollapsibleDelegateTool(tool,delegate)) };
}

export function publicUpdateHistory(progress: Pick<NonNullable<DelegateStatus["progress"]>, "history" | "commentary"> | undefined) {
  const source = Array.isArray(progress?.history) && progress.history.length
    ? progress.history : progress?.commentary ? [progress.commentary] : [];
  return source.slice(-8).filter(entry => typeof entry?.text === "string" && entry.text.trim() &&
    entry.text.length <= 600 && Number.isFinite(Date.parse(entry.updatedAt)));
}

/** Supplied runtime execution only; do not count queue or invent a wall-clock start. */
export function activeExecutionLabel(milliseconds: number | undefined) {
  if (typeof milliseconds !== "number" || !Number.isFinite(milliseconds) || milliseconds < 0) return null;
  const seconds = Math.floor(milliseconds / 1000);
  return seconds < 60 ? seconds + "s" : seconds < 3600 ? Math.floor(seconds / 60) + "m " + seconds % 60 + "s"
    : Math.floor(seconds / 3600) + "h " + Math.floor(seconds % 3600 / 60) + "m";
}
